import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../api/client.js';
import { useSession } from '../auth/SessionProvider.js';
import { Alert, Button, Field, Spinner, TextInput } from '../components/ui.js';
import { MIN_PASSWORD_LENGTH_HINT } from '../lib/passwordHint.js';

interface InvitationLookup {
  email: string;
  fullName: string;
  phone: string | null;
  jobTitle: string | null;
  organizationName: string;
  roleNames: string[];
}

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const { refresh } = useSession();

  const [invitation, setInvitation] = useState<InvitationLookup | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ fullName: '', jobTitle: '', phone: '' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLookupError('This invitation link is missing its token.');
      setLoading(false);
      return;
    }
    apiRequest<InvitationLookup>('/api/auth/invitation/lookup', {
      method: 'POST',
      body: { token },
    })
      .then((result) => {
        setInvitation(result);
        setProfile({
          fullName: result.fullName,
          jobTitle: result.jobTitle ?? '',
          phone: result.phone ?? '',
        });
      })
      .catch((caught: unknown) => {
        setLookupError(
          caught instanceof ApiRequestError
            ? caught.message
            : 'This invitation could not be verified.',
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest('/api/auth/invitation/accept', {
        method: 'POST',
        body: {
          token,
          password,
          fullName: profile.fullName.trim(),
          jobTitle: profile.jobTitle.trim(),
          phone: profile.phone.trim(),
        },
      });
      await refresh();
      navigate('/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Your account could not be activated. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-slate-900">Activate your account</h1>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200">
          {loading ? (
            <Spinner label="Verifying your invitation" />
          ) : lookupError ? (
            <div className="flex flex-col gap-4">
              <Alert variant="error" title="Invitation not valid">
                {lookupError}
              </Alert>
              <p className="text-sm text-slate-600">
                Invitations expire. Ask your administrator to send a new one.
              </p>
              <Button variant="secondary" onClick={() => navigate('/sign-in')}>
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
              {error && <Alert variant="error">{error}</Alert>}

              <div className="rounded-md bg-slate-50 p-3 text-sm">
                <p className="font-medium text-slate-900">{invitation?.email}</p>
                <p className="text-slate-600">{invitation?.organizationName}</p>
                {(invitation?.roleNames.length ?? 0) > 0 && (
                  <p className="text-slate-600">{invitation?.roleNames.join(', ')}</p>
                )}
              </div>

              <p className="text-sm text-slate-600">
                Your access level was set by the administrator who invited you. Ask them if anything
                above looks wrong.
              </p>

              <Field label="Full name" htmlFor="fullName" required>
                <TextInput
                  id="fullName"
                  autoComplete="name"
                  required
                  value={profile.fullName}
                  onChange={(event) => setProfile({ ...profile, fullName: event.target.value })}
                />
              </Field>

              <Field
                label="Job title"
                htmlFor="jobTitle"
                required
                hint="Recorded with your approvals, e.g. Contracting Officer."
              >
                <TextInput
                  id="jobTitle"
                  autoComplete="organization-title"
                  required
                  value={profile.jobTitle}
                  onChange={(event) => setProfile({ ...profile, jobTitle: event.target.value })}
                />
              </Field>

              <Field
                label="Phone"
                htmlFor="phone"
                required
                hint="How VGS reaches you about a repair in progress."
              >
                <TextInput
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  value={profile.phone}
                  onChange={(event) => setProfile({ ...profile, phone: event.target.value })}
                />
              </Field>

              <Field
                label="Choose a password"
                htmlFor="password"
                required
                hint={MIN_PASSWORD_LENGTH_HINT}
              >
                <TextInput
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              <Field label="Confirm password" htmlFor="confirm" required>
                <TextInput
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </Field>

              <Button type="submit" loading={submitting} className="w-full">
                Activate account
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
